import { shownTime } from './round-config'
import { mutate, type Ack } from './api'
import { agreedTimeKey, timeIsAgreed } from './rebook-message'
import { visibleTime } from './business-settings'
import { undoPatch } from './undo'
import { dayLabel } from './ui'
import type { Clean } from './types'

// Visit saves that more than one screen offers, so the Diary and the customer page make exactly the same change with
// exactly the same Undo (customer page redesign, Liam 9 Oct 2026: "dont want to ... introduce new ways"). Each screen
// passes its own redraw.

function alert4xx(ack: Ack): void {
  if (!ack.ok && ack.status >= 400 && ack.status < 500) window.alert(ack.error ?? 'rejected')
}

// Cancel visit, with Undo like every other change to a visit (bug hunt, 8 Oct 2026). Cancelling clears whether the day
// and time were agreed; Undo books it again with both put back, and undo_restore brings back a door record too.
// ask: false when the tap was already the answer (Not done > Cancel this visit); Undo is still offered.
// changed: the screen's redraw once the save lands; refused: its redraw after a refusal (already shown).
export function cancelVisitWithUndo(cl: Clean, o: { close?: () => void; ask?: string | false; undoText?: string; changed: () => void; refused: () => void }): void {
  if (o.ask !== false && !window.confirm(o.ask ?? `Cancel the ${dayLabel(cl.date)} visit for ${cl.customer_name}?`)) return
  o.close?.()
  const was = { version: cl.version, confirmed: cl.confirmed, time_agreed_for: timeIsAgreed(cl) ? cl.time_agreed_for ?? null : null }
  void mutate({ label: `Cancel: ${cl.customer_name}`, method: 'PATCH', path: `/api/cleans/${cl.id}`, body: { version: cl.version, state: 'cancelled' }, onAck: o.changed, onConflict: o.changed })
    .then((ack) => {
      // Refused: drawn again as Roundly has it, its buttons live again.
      if (!ack.ok && ack.status >= 400 && ack.status < 500) { alert4xx(ack); o.refused(); return }
      undoPatch({
        text: o.undoText ?? `Cancelled the ${dayLabel(cl.date)} visit for ${cl.customer_name ?? 'them'}`,
        label: `Undo cancel: ${cl.customer_name ?? ''} ${cl.date}`,
        path: `/api/cleans/${cl.id}`,
        ack,
        version: was.version,
        body: { state: 'booked', confirmed: was.confirmed, time_agreed_for: was.time_agreed_for, undo_restore: true },
        after: o.refused,
      })
    })
}

// Mark confirmed: the day is agreed, with Undo. The Diary's button and its ⋯ menu's line (a visit nobody has been asked
// about yet, agreed another way: on the phone, at the door) are this one save.
export function markDayAgreed(cl: Clean, button: HTMLElement, after: () => void): void {
  button.setAttribute('disabled', 'disabled')
  void mutate({
    label: `Confirm visit: ${cl.customer_name ?? ''} ${cl.date}`,
    method: 'PATCH',
    path: `/api/cleans/${cl.id}`,
    body: { version: cl.version, confirmed: 1 },
    onAck: after,
    onConflict: after,
  }).then((ack) => {
    if (!ack.ok && ack.status >= 400 && ack.status < 500) button.removeAttribute('disabled')
    alert4xx(ack)
    undoPatch({ text: `Marked ${cl.customer_name ?? 'visit'} confirmed`, label: `Undo confirmed: ${cl.customer_name ?? ''} ${cl.date}`, path: `/api/cleans/${cl.id}`, ack, version: cl.version,
      body: { confirmed: 0 }, after })
  })
}

// The day agreed and a time set but not agreed: one tap agrees that exact time, the same save as the booking form's
// "Time agreed" tick, with Undo (Release 2, JOINT-PLAN 4e).
export function markTimeAgreed(cl: Clean, button: HTMLElement, after: () => void): void {
  // A round with no set times has no time to agree: visibleTime is empty, so there is no key and nothing is saved.
  const timeKey = agreedTimeKey(cl.date, visibleTime(cl))
  if (!timeKey) return
  button.setAttribute('disabled', 'disabled')
  void mutate({
    label: `Time agreed: ${cl.customer_name ?? ''} ${cl.date} ${cl.time_slot?.trim() ?? ''}`,
    method: 'PATCH',
    path: `/api/cleans/${cl.id}`,
    body: { version: cl.version, time_agreed_for: timeKey },
    onAck: after,
    onConflict: after,
  }).then((ack) => {
    if (!ack.ok && ack.status >= 400 && ack.status < 500) button.removeAttribute('disabled')
    alert4xx(ack)
    undoPatch({ text: `${cl.customer_name ?? 'Visit'}: time agreed, ${shownTime(cl.time_slot?.trim() ?? '')} on ${dayLabel(cl.date)}`, label: `Undo time agreed: ${cl.customer_name ?? ''} ${cl.date}`,
      path: `/api/cleans/${cl.id}`, ack, version: cl.version, body: { time_agreed_for: null }, after })
  })
}

// Agreed, in the visit card (design Q, Liam 4 Nov 2026: "instead of she said yes it should just say agreed"): one tap
// agrees whatever is still open, the day, the time offered, or both in one save, with one Undo putting back exactly what
// it changed. The day alone and the time alone are the same saves as markDayAgreed and markTimeAgreed.
export function markAgreed(cl: Clean, button: HTMLElement, after: () => void): void {
  const timeKey = agreedTimeKey(cl.date, visibleTime(cl))
  const day = !cl.confirmed
  const time = !!timeKey && cl.time_agreed_for !== timeKey
  if (day && !time) { markDayAgreed(cl, button, after); return }
  if (!day && time) { markTimeAgreed(cl, button, after); return }
  if (!day && !time) return
  button.setAttribute('disabled', 'disabled')
  void mutate({
    label: `Agreed: ${cl.customer_name ?? ''} ${cl.date} ${cl.time_slot?.trim() ?? ''}`,
    method: 'PATCH',
    path: `/api/cleans/${cl.id}`,
    body: { version: cl.version, confirmed: 1, time_agreed_for: timeKey },
    onAck: after,
    onConflict: after,
  }).then((ack) => {
    if (!ack.ok && ack.status >= 400 && ack.status < 500) button.removeAttribute('disabled')
    alert4xx(ack)
    undoPatch({ text: `${cl.customer_name ?? 'Visit'}: agreed, ${dayLabel(cl.date)} at ${shownTime(cl.time_slot?.trim() ?? '')}`, label: `Undo agreed: ${cl.customer_name ?? ''} ${cl.date}`,
      path: `/api/cleans/${cl.id}`, ack, version: cl.version, body: { confirmed: 0, time_agreed_for: cl.time_agreed_for ?? null }, after })
  })
}
